import React, { useEffect, useRef, useState } from 'react'

// components
import SortTable from '../components/SortTable'
import PositionChart, { getPositionChartHeight } from '../components/PositionChart'
import WeatherForecast from '../components/WeatherForecast'
import KioskControls from '../components/KioskControls'
import KioskQrCode, { QR_CODE_CLASS } from '../components/KioskQrCode'
import Icon from '../components/Icon'

// redux
import { useSelector, useDispatch } from 'react-redux'
import { getKioskOptions } from '../redux/options/selectors'
import { setKioskOption } from '../redux/options/actions'
import { getGroupStandings } from '../redux/data/selectors'
import groupTypes from '../redux/options/groups'

// tools
import { ZOOM, OPTION_ZOOM, OPTION_FIT, getZoom, getShowCharts, getShowWeather, getAutoFit } from '../tools/Kiosk'
import { splitIntoColumns } from '../tools/Columns'

/**
 * Columns of the standings, the same as on the group page
 * @type {Object<string, boolean>}
 */
const COLUMN_OPTIONS = {
  pos: true,
  car: true,
  laptime: true,
  penalty: false,
  lap: false,
  time: true
}

/**
 * Most cars in one list of a race without groups; a longer list is split in two halves, numbered
 * on, that sit next to each other, next to the chart, where there are enough columns for that
 * @type {number}
 */
const MAX_LIST = 12

/**
 * Fewest columns for a split list: the chart and both halves next to each other; with fewer the
 * whole list goes next to or below the chart
 * @type {number}
 */
const SPLIT_COLUMNS = 3

/**
 * Variant of a long list of all cars: the whole list, shown with too few columns for the halves
 * @type {string}
 */
const LIST_WHOLE = 'whole'

/**
 * Variant of a long list of all cars: one of its halves, shown with enough columns for both
 * @type {string}
 */
const LIST_HALF = 'half'

/**
 * Get the blocks to show: per group of an official race its header, chart and list; for a race
 * without groups, all cars, its chart and the list without headers. A long list is there both
 * whole and in two halves, of which placeGroups shows the one that suits the columns
 * @param {Array<{ group: number|string, standings: Array<Object>, laps: Array<Object> }>} groups standings per group
 * @param {boolean} charts whether the position charts are shown
 * @returns {Array<{ key: string, title: number|string, chart: boolean, standings: Array<Object>, laps: Array<Object>, only: boolean, variant: string }>} blocks
 */
const getBlocks = ( groups, charts ) => groups.flatMap( ( { group, standings, laps } ) =>
{
  const chart = charts && standings.length > 1
  if ( group !== '' ) return [ { key: String( group ), title: group, chart, standings, laps, only: false, variant: '' } ]

  const list = { title: '', chart: false, laps: [], only: false }
  const length = Math.ceil( standings.length / 2 )
  const parts = standings.length > MAX_LIST
    ? [
      { ...list, key: 'all-whole', standings, variant: LIST_WHOLE },
      { ...list, key: 'all-0', standings: standings.slice( 0, length ), variant: LIST_HALF },
      { ...list, key: 'all-1', standings: standings.slice( length ), variant: LIST_HALF }
    ]
    : [ { ...list, key: 'all', standings, variant: '' } ]

  return chart ? [ { key: 'all-chart', title: '', chart: true, standings, laps, only: true, variant: '' }, ...parts ] : parts
} )

/**
 * Show either the whole long list of all cars or its halves, as suits the number of columns
 * @param {HTMLElement} list grid with the groups
 * @param {number} columns number of columns
 * @returns {Array<HTMLElement>} the groups that show, without the QR code
 */
const showVariants = ( list, columns ) =>
{
  const hidden = columns >= SPLIT_COLUMNS ? LIST_WHOLE : LIST_HALF
  return Array.from( list.children ).filter( group =>
  {
    group.hidden = group.dataset.variant === hidden
    return !group.hidden && !group.classList.contains( QR_CODE_CLASS )
  } )
}

/**
 * Get the height of a block in grid rows, with the room below it
 * @param {HTMLElement} block group or QR code
 * @param {number} row height of a grid row in pixels
 * @returns {number} grid rows, at least one
 */
const getRows = ( block, row ) =>
{
  const gap = parseFloat( getComputedStyle( block ).marginBottom ) || 0
  return Math.max( 1, Math.ceil( ( block.offsetHeight + gap ) / row ) )
}

/**
 * Place the QR code below the lowest column, where there is the most room; the rightmost of equally
 * low columns, so an empty column takes it
 * @param {HTMLElement} list grid with the groups
 * @param {Array<{ column: number, row: number }>} places first column and row of each group
 * @param {Array<{ height: number, width: number }>} blocks height and width of each group
 * @param {number} columns number of columns
 * @param {number} row height of a grid row in pixels
 * @returns {void}
 */
const placeQrCode = ( list, places, blocks, columns, row ) =>
{
  const qr = list.querySelector( `.${ QR_CODE_CLASS }` )
  if ( !qr ) return

  const bottoms = new Array( columns ).fill( 0 )
  places.forEach( ( place, index ) =>
  {
    for ( let column = place.column; column < place.column + blocks[ index ].width; column++ )
    {
      bottoms[ column ] = Math.max( bottoms[ column ], place.row + blocks[ index ].height )
    }
  } )

  const lowest = bottoms.reduce( ( best, bottom, column ) => bottom <= bottoms[ best ] ? column : best, 0 )
  qr.style.gridColumn = `${ lowest + 1 } / span 1`
  qr.style.gridRow = `${ bottoms[ lowest ] + 1 } / span ${ getRows( qr, row ) }`
}

/**
 * Height in pixels of each position in the charts, lower than elsewhere so more fits the screen
 * @type {number}
 */
const CHART_ROW = 16

/**
 * Narrowest column in em, as wide as the standings with their four columns need; a zoom makes the
 * columns wider, so fewer fit
 * @type {number}
 */
const COLUMN_WIDTH = 36

/**
 * Get the number of columns that fit the width of the grid, and set them and their width on the grid
 * @param {HTMLElement} list grid with the groups
 * @returns {number} number of columns, at least one
 */
const setColumns = ( list ) =>
{
  const style = getComputedStyle( list )
  const gap = parseFloat( style.columnGap ) || 0
  const column = COLUMN_WIDTH * parseFloat( style.fontSize )
  const columns = Math.max( 1, Math.floor( ( list.clientWidth + gap ) / ( column + gap ) ) )
  list.style.gridTemplateColumns = `repeat( ${ columns }, minmax( 0, 1fr ) )`
  list.dataset.columnWidth = String( Math.round( list.clientWidth / columns ) )
  return columns
}

/**
 * Get the height the groups can take without scrolling: the screen less the room around the board,
 * in the zoomed size of the groups
 * @param {HTMLElement} list grid with the groups
 * @returns {number} height in pixels at the zoom of the groups
 */
const getScreenHeight = ( list ) =>
{
  const board = list.closest( '.kiosk-board' )
  const style = board ? getComputedStyle( board ) : null
  const padding = style ? parseFloat( style.paddingTop ) + parseFloat( style.paddingBottom ) : 0
  const zoom = list.offsetWidth ? list.getBoundingClientRect().width / list.offsetWidth : 1
  return ( window.innerHeight - padding ) / zoom
}

/**
 * Lay out the groups in their order over the columns, each column a run of groups from top to
 * bottom, so that the highest column is as low as possible. A group whose chart and list don't fit
 * the screen height above each other takes two columns, where there are two, with the chart next to
 * the list. Each group spans as many of the small grid rows of Kiosk.less as it is high. The QR code
 * goes below them where there is the most room
 * @param {HTMLElement} list grid with the groups
 * @returns {void}
 */
const placeGroups = ( list ) =>
{
  const row = parseFloat( getComputedStyle( list ).gridAutoRows ) || 1
  const columns = setColumns( list )
  const screen = getScreenHeight( list )
  const groups = showVariants( list, columns )

  /**
   * Measure the groups at their width, as it sets their height
   * @param {boolean} allowWide whether a group too high for the screen may take two columns
   * @returns {Array<{ height: number, width: number }>} height in grid rows and width in columns of each group
   */
  const measure = ( allowWide ) => groups.map( group =>
  {
    // one column first; too high for the screen with a chart, then two
    delete group.dataset.wide
    group.style.gridColumn = '1 / span 1'
    const wide = allowWide && columns > 1 && group.classList.contains( 'has-chart' ) && group.offsetHeight > screen
    if ( wide )
    {
      group.dataset.wide = 'true'
      group.style.gridColumn = '1 / span 2'
    }

    return { height: getRows( group, row ), width: wide ? 2 : 1 }
  } )

  // all groups one column wide when the wide ones don't fit between the others
  let blocks = measure( true )
  let places = splitIntoColumns( blocks, columns )
  if ( !places.length )
  {
    blocks = measure( false )
    places = splitIntoColumns( blocks, columns )
  }

  places.forEach( ( place, index ) =>
  {
    const group = groups[ index ]
    group.style.gridColumn = `${ place.column + 1 } / span ${ blocks[ index ].width }`
    group.style.gridRow = `${ place.row + 1 } / span ${ blocks[ index ].height }`
  } )

  placeQrCode( list, places, blocks, columns, row )
}

/**
 * Custom property with the zoom of the groups, for sizes that stay the same on screen at any zoom
 * @type {string}
 */
const ZOOM_PROPERTY = '--kiosk-zoom'

/**
 * Steps in which fitting to the screen first looks for a zoom that fits, before going up a step of
 * the slider at a time
 * @type {number}
 */
const FIT_STEP = 0.05

/**
 * Round a zoom to whole percentages
 * @param {number} zoom zoom
 * @returns {number} rounded zoom
 */
const roundZoom = ( zoom ) => Number( zoom.toFixed( 2 ) )

/**
 * Find the largest zoom at which the page needs no scroll bars: from the largest zoom down in fit
 * steps, then up again a slider step at a time. The groups are placed at each zoom tried, and at
 * the zoom found at the end
 * @param {HTMLElement} content element with the zoom
 * @param {HTMLElement} list grid with the groups
 * @returns {number} zoom, the smallest when nothing fits
 */
const findFittingZoom = ( content, list ) =>
{
  const page = document.documentElement

  /**
   * Show the groups at a zoom and check whether they fit the screen
   * @param {number} zoom zoom to try
   * @returns {boolean} whether the page needs no scroll bars
   */
  const fits = ( zoom ) =>
  {
    content.style.zoom = zoom
    content.style.setProperty( ZOOM_PROPERTY, zoom )
    placeGroups( list )
    return page.scrollHeight <= page.clientHeight && page.scrollWidth <= page.clientWidth
  }

  // no scroll bar while trying: one that shows for a zoom that is too large would leave less width,
  // and so fewer columns, for the zooms tried after it
  const overflow = page.style.overflowY
  page.style.overflowY = 'hidden'

  let best = ZOOM.min
  for ( let zoom = ZOOM.max; zoom >= ZOOM.min; zoom = roundZoom( zoom - FIT_STEP ) )
  {
    if ( fits( zoom ) )
    {
      best = zoom
      break
    }
  }

  for ( let zoom = roundZoom( best + ZOOM.step ); zoom < best + FIT_STEP && zoom <= ZOOM.max; zoom = roundZoom( zoom + ZOOM.step ) )
  {
    if ( !fits( zoom ) ) break
    best = zoom
  }

  // show the zoom found before the scroll bar may come back
  fits( best )
  page.style.overflowY = overflow
  return best
}

/**
 * Kiosk screen: the weather forecast and the standings of every group at once, in as many columns
 * as fit the width of the screen at the chosen zoom; a group too high for the screen with its chart
 * takes two columns; with the buttons to zoom, fit to the screen and open the options
 * @returns {JSX.Element} kiosk page
 */
const KioskPage = () =>
{
  const dispatch = useDispatch()
  // redux
  const groups = useSelector( getGroupStandings )
  const options = useSelector( getKioskOptions )
  const charts = getShowCharts( options )
  const weather = getShowWeather( options )
  const zoom = getZoom( options )
  const autoFit = getAutoFit( options )
  const listRef = useRef( null )
  const contentRef = useRef( null )
  // width of the columns the groups were last placed in, which the charts are drawn at
  const [ columnWidth, setColumnWidth ] = useState( '' )

  /**
   * Draw the charts again when the groups were placed in columns of another width
   * @returns {void}
   */
  const updateColumnWidth = () =>
  {
    if ( listRef.current ) setColumnWidth( listRef.current.dataset.columnWidth || '' )
  }

  // whether to fit for the listeners, which are bound once per set of groups
  const autoFitRef = useRef( autoFit )
  autoFitRef.current = autoFit

  /**
   * Zoom so that everything fits the screen without scroll bars
   * @returns {void}
   */
  const fit = () =>
  {
    if ( !contentRef.current || !listRef.current ) return
    dispatch( setKioskOption( OPTION_ZOOM, findFittingZoom( contentRef.current, listRef.current ) ) )
    updateColumnWidth()
  }

  /**
   * Fit to the screen now, and again whenever the groups or the screen change, until zoomed by hand
   * @returns {void}
   */
  const onFit = () =>
  {
    dispatch( setKioskOption( OPTION_FIT, true ) )
    fit()
  }

  // fit again when laps come in or the charts or the weather are switched, while fitting by itself
  useEffect( () =>
  {
    if ( !autoFit ) return
    const frame = requestAnimationFrame( fit )
    return () => cancelAnimationFrame( frame )
  }, [ groups, charts, weather, autoFit ] )

  // place the groups again whenever one changes height, such as when laps come in or a chart is drawn,
  // or the screen changes size; on the next frame, as placing them can resize them again, for
  // instance when a scroll bar appears
  useEffect( () =>
  {
    const list = listRef.current
    if ( !list ) return

    // while fitting by itself, any change of size fits again, such as the fonts loading
    let frame = 0
    const schedule = () =>
    {
      cancelAnimationFrame( frame )
      frame = requestAnimationFrame( () =>
      {
        if ( autoFitRef.current ) return fit()
        placeGroups( list )
        updateColumnWidth()
      } )
    }

    const observer = new ResizeObserver( schedule )
    observer.observe( list )
    Array.from( list.children ).forEach( group => observer.observe( group ) )
    window.addEventListener( 'resize', schedule )
    if ( document.fonts ) document.fonts.ready.then( schedule )

    return () =>
    {
      cancelAnimationFrame( frame )
      observer.disconnect()
      window.removeEventListener( 'resize', schedule )
    }
  }, [ groups, charts ] )

  return (
    <div className="kiosk-board">
      { /* the weather in the bottom left corner, opposite the buttons; it credits the weather of the
        charts too, so without a forecast, such as for an earlier race, it only shows with the charts */ }
      { weather && (
        <div className="kiosk-weather">
          <WeatherForecast credit={ charts }/>
        </div>
      ) }
      <KioskControls onFit={ onFit }/>
      <div className="kiosk-content" style={ { zoom, [ ZOOM_PROPERTY ]: zoom } } ref={ contentRef }>
        { !groups.length && <p className="kiosk-empty">Waiting for laps…</p> }
        { groups.length > 0 && (
          <div className="kiosk-groups" ref={ listRef }>
            { getBlocks( groups, charts ).map( ( { key, title, chart, standings, laps, only, variant } ) => (
              // a race without groups shows all cars without a header or edge; a chart on its own
              // never takes two columns, as it has no list to go next to
              <section className={ [ 'kiosk-group', chart && !only ? 'has-chart' : '', title === '' ? 'all-cars' : '' ].join( ' ' ).trim() } data-variant={ variant || undefined } key={ key }>
                { title !== '' && <h2 className="kiosk-group-title"><Icon icon="hashtag"/>{ title }</h2> }
                <div className="kiosk-group-body">
                  { chart && (
                    // the room of the chart is kept before it is drawn, so the groups are placed right
                    // away; drawn, it may take more
                    <div className="kiosk-chart" style={ { minHeight: getPositionChartHeight( standings.length, weather, CHART_ROW ) } }>
                      { /* another zoom or other columns change the width of the chart, so it is drawn again */ }
                      <PositionChart key={ `${ zoom }-${ columnWidth }` } rows={ laps } weather={ weather } zoom={ false } credit={ false } personal={ false } rowHeight={ CHART_ROW }/>
                    </div>
                  ) }
                  { !only && (
                    <SortTable
                      className="kiosk-table"
                      data={ standings }
                      context={ groupTypes.KIOSK }
                      column_options={ COLUMN_OPTIONS }
                      clickRow={ false }
                      columnSort={ false }
                      paging={ false }
                      personal={ false }
                    />
                  ) }
                </div>
              </section>
            ) ) }
            { /* placed by placeGroups where the columns leave the most room */ }
            <KioskQrCode/>
          </div>
        ) }
      </div>
    </div>
  )
}

export default KioskPage
